/** Great-circle + circle geometry helpers for route lines and range rings. */

const R_KM = 6371.0088;
const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

/** Interpolate `n` points along the great circle from a to b (inclusive). */
export function greatCirclePoints(
  a: [number, number],
  b: [number, number],
  n = 64,
): [number, number][] {
  const [lon1, lat1] = [a[0] * D2R, a[1] * D2R];
  const [lon2, lat2] = [b[0] * D2R, b[1] * D2R];
  const d =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((lat2 - lat1) / 2) ** 2 +
          Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2,
      ),
    );
  if (d === 0 || !Number.isFinite(d)) return [a, b];
  const pts: [number, number][] = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
    const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
    const z = A * Math.sin(lat1) + B * Math.sin(lat2);
    const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
    const lon = Math.atan2(y, x);
    pts.push([lon * R2D, lat * R2D]);
  }
  return pts;
}

/** Polygon (ring) of `steps` points at `radiusKm` around a center. */
export function circlePolygon(
  center: [number, number],
  radiusKm: number,
  steps = 96,
): [number, number][] {
  const [lon, lat] = center;
  const latR = lat * D2R;
  const ring: [number, number][] = [];
  const d = radiusKm / R_KM;
  for (let i = 0; i <= steps; i++) {
    const brng = (i / steps) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(latR) * Math.cos(d) + Math.cos(latR) * Math.sin(d) * Math.cos(brng),
    );
    const lon2 =
      lon * D2R +
      Math.atan2(
        Math.sin(brng) * Math.sin(d) * Math.cos(latR),
        Math.cos(d) - Math.sin(latR) * Math.sin(lat2),
      );
    ring.push([lon2 * R2D, lat2 * R2D]);
  }
  return ring;
}

export function distanceKm(a: [number, number], b: [number, number]): number {
  const dLat = (b[1] - a[1]) * D2R;
  const dLon = (b[0] - a[0]) * D2R;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a[1] * D2R) * Math.cos(b[1] * D2R) * Math.sin(dLon / 2) ** 2;
  return 2 * R_KM * Math.asin(Math.sqrt(s));
}

/**
 * Flat-earth project a point `distNm` nautical miles along `courseDeg` from
 * (lat, lon). 1 NM = 1/60° latitude; longitude is divided by cos(lat) so the
 * projection stays in degrees. Accurate enough for the short distances used
 * by approach-procedure overlays (<10 NM), far cheaper than the spherical
 * great-circle math above.
 */
export function offsetPoint(
  lat: number,
  lon: number,
  courseDeg: number,
  distNm: number,
): [number, number] {
  const rad = (courseDeg * Math.PI) / 180;
  const dLat = (distNm / 60) * Math.cos(rad);
  const dLon = (distNm / 60) * Math.sin(rad) / Math.cos((lat * Math.PI) / 180);
  return [lon + dLon, lat + dLat];
}

/**
 * Final Approach Fix (FAF) location: `distNm` nautical miles back from the
 * runway threshold along the back-azimuth of the localizer course. Used to
 * anchor the centreline approach overlay.
 */
export function finalApproachFix(
  thresholdLat: number,
  thresholdLon: number,
  courseDeg: number,
  distNm: number,
): [number, number] {
  return offsetPoint(thresholdLat, thresholdLon, (courseDeg + 180) % 360, distNm);
}
